/* main.js - global behaviours for all pages
   Provides shared utilities, lazy video hydration, custom controls, and search.
*/
(() => {
  "use strict";

  /* -------------------------
     Utilities
     ------------------------- */
  const $ = (sel, ctx = window.document) => ctx.querySelector(sel);
  const $$ = (sel, ctx = window.document) =>
    Array.from(ctx.querySelectorAll(sel));
  const safe = (fn) => {
    try {
      return fn();
    } catch (e) {
      return undefined;
    }
  };
  const slugify = (text) =>
    text
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9\s-]/g, "")
      .replace(/\s+/g, "-")
      .replace(/-+/g, "-");
  const debounce = (fn, wait = 150) => {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(null, args), wait);
    };
  };

  const scrollRevealChecks = [];
  let scrollRevealRefreshBound = false;

  const bindScrollRevealRefresh = () => {
    if (scrollRevealRefreshBound || !window.ScrollTrigger) return;
    scrollRevealRefreshBound = true;
    ScrollTrigger.addEventListener(
      "refresh",
      debounce(() => {
        scrollRevealChecks.forEach((check) => check());
      }, 80),
    );
  };

  const scheduleScrollTriggerRefresh = debounce(() => {
    if (window.ScrollTrigger) ScrollTrigger.refresh();
  }, 150);

  /* -------------------------
     Media helpers
     ------------------------- */
  const requestMutedInlinePlayback = (videoEl) => {
    if (!videoEl) return;
    try {
      videoEl.muted = true;
      videoEl.setAttribute("muted", "");
      videoEl.playsInline = true;
      videoEl.setAttribute("playsinline", "");
      videoEl.setAttribute("webkit-playsinline", "");
      videoEl.play().catch(() => {
        const resume = () => {
          videoEl.play().finally(() => {
            window.removeEventListener("pointerdown", resume);
            window.removeEventListener("touchstart", resume);
          });
        };
        window.addEventListener("pointerdown", resume, { once: true });
        window.addEventListener("touchstart", resume, { once: true });
      });
    } catch (e) {}
  };

  const hydrateVideo = (videoEl) => {
    let updated = false;

    if (videoEl.dataset && videoEl.dataset.poster) {
      videoEl.poster = videoEl.dataset.poster;
      videoEl.removeAttribute("data-poster");
    }

    const sources = videoEl.querySelectorAll("source[data-src]");
    sources.forEach((sourceEl) => {
      if (!sourceEl.getAttribute("src")) {
        sourceEl.setAttribute("src", sourceEl.getAttribute("data-src"));
        updated = true;
      }
      sourceEl.removeAttribute("data-src");
    });

    if (videoEl.hasAttribute("data-src") && !videoEl.querySelector("source")) {
      videoEl.setAttribute("src", videoEl.getAttribute("data-src"));
      videoEl.removeAttribute("data-src");
      updated = true;
    }

    if (!updated) return;

    safe(() => videoEl.load());
    const wantsAutoplay = videoEl.autoplay || videoEl.hasAttribute("autoplay");
    if (!wantsAutoplay) return;

    requestMutedInlinePlayback(videoEl);
    videoEl.addEventListener(
      "canplay",
      () => requestMutedInlinePlayback(videoEl),
      { once: true },
    );
    videoEl.addEventListener(
      "loadeddata",
      () => requestMutedInlinePlayback(videoEl),
      { once: true },
    );
  };

  const hydrateLazyVideos = () => {
    $$("video").forEach(hydrateVideo);
  };

  const initProgressiveImages = () => {
    $$("img.progressive-img[data-hd-src]").forEach((img) => {
      const hdSrc = img.getAttribute("data-hd-src");
      if (!hdSrc || img.dataset.hdDone === "true") return;

      const applyHd = () => {
        if (img.dataset.hdDone === "true") return;
        img.dataset.hdDone = "true";
        img.classList.add("is-hd-loading");

        const hd = new Image();
        hd.decoding = "async";
        hd.onload = () => {
          img.addEventListener(
            "load",
            () => {
              img.classList.remove("is-hd-loading");
              img.classList.add("is-hd-ready");
            },
            { once: true },
          );
          img.src = hdSrc;
          img.removeAttribute("data-hd-src");
        };
        hd.onerror = () => {
          img.classList.remove("is-hd-loading");
          img.classList.add("is-hd-failed");
          img.removeAttribute("data-hd-src");
        };
        hd.src = hdSrc;
      };

      if (!("IntersectionObserver" in window)) {
        applyHd();
        return;
      }

      const io = new IntersectionObserver(
        (entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          io.disconnect();
          applyHd();
        },
        { rootMargin: "320px 0px" },
      );
      io.observe(img);
    });
  };

  /* -------------------------
     Global behaviour
     ------------------------- */
  const pauseAllOnHidden = () => {
    document.addEventListener("visibilitychange", () => {
      if (!document.hidden) return;
      $$("video").forEach((videoEl) => {
        try {
          videoEl.pause();
        } catch (e) {}
      });
    });
  };

  const initNavDropdowns = () => {
    const dropdowns = Array.from(
      document.querySelectorAll("[data-nav-dropdown]"),
    );
    if (!dropdowns.length) return;

    const closeAll = (except) => {
      dropdowns.forEach((dropdown) => {
        if (dropdown === except) return;
        dropdown.classList.remove("is-open");
        dropdown
          .querySelector(".nav-dropdown__trigger")
          ?.setAttribute("aria-expanded", "false");
      });
    };

    dropdowns.forEach((dropdown) => {
      const trigger = dropdown.querySelector(".nav-dropdown__trigger");
      if (!trigger) return;

      trigger.addEventListener("click", (e) => {
        e.stopPropagation();
        const isOpen = dropdown.classList.contains("is-open");
        closeAll();
        if (!isOpen) {
          dropdown.classList.add("is-open");
          trigger.setAttribute("aria-expanded", "true");
        }
      });
    });

    document.addEventListener("click", () => closeAll());

    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape") closeAll();
    });

    return { closeAll };
  };

  const initNavCurrentLink = () => {
    const nav = document.getElementById("gh-navigation");
    if (!nav) return;

    const normalizePath = (pathname) => {
      const stripped = pathname
        .replace(/\/index\.html$/i, "")
        .replace(/\/+$/, "");
      return stripped || "/";
    };

    const currentPath = normalizePath(window.location.pathname);

    nav.querySelectorAll("a.ax-nav__link[data-nav-path]").forEach((link) => {
      let linkPath;
      try {
        linkPath = normalizePath(
          new URL(link.getAttribute("href"), window.location.origin).pathname,
        );
      } catch {
        return;
      }

      if (linkPath === currentPath) {
        link.classList.add("is-active");
      }
    });
  };

  const initMobileNav = () => {
    const toggle = document.querySelector("[data-nav-toggle]");
    const menu = document.querySelector("[data-nav-menu]");
    const overlay = document.querySelector("[data-nav-overlay]");

    if (!toggle || !menu) return;

    const openMenu = () => {
      menu.classList.add("is-open");
      toggle.classList.add("is-active");
      toggle.setAttribute("aria-expanded", "true");
      overlay?.classList.add("is-visible");
      document.body.classList.add("menu-open");
    };

    const closeMenu = () => {
      menu.classList.remove("is-open");
      toggle.classList.remove("is-active");
      toggle.setAttribute("aria-expanded", "false");
      overlay?.classList.remove("is-visible");
      document.body.classList.remove("menu-open");
      document
        .querySelectorAll("[data-nav-dropdown].is-open")
        .forEach((dropdown) => {
          dropdown.classList.remove("is-open");
          dropdown
            .querySelector(".nav-dropdown__trigger")
            ?.setAttribute("aria-expanded", "false");
        });
    };

    const toggleMenu = () => {
      if (menu.classList.contains("is-open")) {
        closeMenu();
      } else {
        openMenu();
      }
    };

    // Toggle on button click
    toggle.addEventListener("click", toggleMenu);

    // Close on overlay click
    overlay?.addEventListener("click", closeMenu);

    // Close on escape key
    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape" && menu.classList.contains("is-open")) {
        closeMenu();
      }
    });

    // Close menu on window resize (when going to desktop)
    window.addEventListener(
      "resize",
      debounce(() => {
        if (window.innerWidth >= 901 && menu.classList.contains("is-open")) {
          closeMenu();
        }
      }, 100),
    );

    // Close menu when clicking a link
    menu.querySelectorAll("a").forEach((link) => {
      link.addEventListener("click", closeMenu);
    });
  };

  const initScrollNav = () => {
    const nav = document.querySelector(".gh-navigation-inner");
    if (!nav) return;

    // Only apply on mobile
    const isMobileView = () => window.innerWidth < 768;

    let lastScrollY = window.scrollY;
    let ticking = false;
    const scrollThreshold = 10; // Minimum scroll amount to trigger hide/show

    const updateNav = () => {
      const currentScrollY = window.scrollY;
      const scrollDiff = currentScrollY - lastScrollY;

      // Only apply behavior on mobile
      if (!isMobileView()) {
        nav.classList.remove("is-hidden");
        lastScrollY = currentScrollY;
        ticking = false;
        return;
      }

      // At top of page, always show
      if (currentScrollY <= 0) {
        nav.classList.remove("is-hidden");
        lastScrollY = currentScrollY;
        ticking = false;
        return;
      }

      // Check if scroll amount exceeds threshold
      if (Math.abs(scrollDiff) < scrollThreshold) {
        ticking = false;
        return;
      }

      // Scrolling down - hide nav
      if (scrollDiff > 0) {
        nav.classList.add("is-hidden");
      }
      // Scrolling up - show nav
      else {
        nav.classList.remove("is-hidden");
      }

      lastScrollY = currentScrollY;
      ticking = false;
    };

    const onScroll = () => {
      if (!ticking) {
        requestAnimationFrame(updateNav);
        ticking = true;
      }
    };

    window.addEventListener("scroll", onScroll, { passive: true });

    // Reset on resize
    window.addEventListener(
      "resize",
      debounce(() => {
        if (!isMobileView()) {
          nav.classList.remove("is-hidden");
        }
      }, 100),
    );
  };

  const initBlogSearch = () => {
    const input = document.querySelector("[data-blog-search-input]");
    if (!input) return;

    const items = Array.from(
      document.querySelectorAll(".blog-card, .blog-featured-card"),
    );
    if (!items.length) return;

    const status = document.querySelector("[data-blog-search-status]");
    const store = items.map((item) => ({
      node: item,
      text: item.textContent.toLowerCase(),
      tags: (item.dataset.tags || "")
        .split(",")
        .map((t) => t.trim().toLowerCase())
        .filter(Boolean),
    }));

    // Filter state
    let selectedTags = [];

    const update = () => {
      const query = input.value.trim().toLowerCase();
      let matches = 0;

      store.forEach(({ node, text, tags }) => {
        const matchesQuery = !query || text.includes(query);
        const matchesTags =
          !selectedTags.length ||
          selectedTags.some((slug) =>
            tags.some((t) => t === slug || slugify(t) === slug),
          );
        const match = matchesQuery && matchesTags;
        node.style.display = match ? "" : "none";
        if (match) matches += 1;
      });

      if (!status) return;
      if ((query || selectedTags.length) && matches === 0) {
        status.hidden = false;
        status.classList.add("is-visible");
      } else {
        status.hidden = true;
        status.classList.remove("is-visible");
      }
    };

    input.addEventListener("input", update);

    // Filter toggle
    const filterContainer = document.querySelector("[data-blog-filter]");
    if (!filterContainer) return;

    const toggle = filterContainer.querySelector("[data-blog-filter-toggle]");
    const dropdown = filterContainer.querySelector(
      "[data-blog-filter-dropdown]",
    );
    const selectedContainer = filterContainer.querySelector(
      "[data-blog-filter-selected]",
    );
    if (!toggle || !dropdown || !selectedContainer) return;

    const openDropdown = () => {
      dropdown.hidden = false;
      toggle.setAttribute("aria-expanded", "true");
    };

    const closeDropdown = () => {
      dropdown.hidden = true;
      toggle.setAttribute("aria-expanded", "false");
    };

    toggle.addEventListener("click", () => {
      if (dropdown.hidden) {
        openDropdown();
      } else {
        closeDropdown();
      }
    });

    // Close dropdown when clicking outside
    document.addEventListener("click", (e) => {
      if (!filterContainer.contains(e.target)) {
        closeDropdown();
      }
    });

    // Close on Escape
    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape") closeDropdown();
    });

    const renderSelectedTags = () => {
      selectedContainer.innerHTML = "";
      selectedTags.forEach((slug) => {
        const option = dropdown.querySelector(`[data-tag-slug="${slug}"]`);
        const name = option ? option.textContent : slug;

        const chip = document.createElement("span");
        chip.className = "blog-filter-chip";
        chip.dataset.tagSlug = slug;

        const label = document.createElement("span");
        label.textContent = name;
        chip.appendChild(label);

        const closeBtn = document.createElement("button");
        closeBtn.type = "button";
        closeBtn.className = "blog-filter-chip-close";
        closeBtn.setAttribute("aria-label", `Remove ${name} filter`);
        closeBtn.innerHTML =
          '<svg width="10" height="10" viewBox="0 0 10 10" fill="none" aria-hidden="true"><path d="M1 1l8 8M9 1l-8 8" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>';
        closeBtn.addEventListener("click", () => {
          selectedTags = selectedTags.filter((t) => t !== slug);
          renderSelectedTags();
          update();
        });
        chip.appendChild(closeBtn);

        selectedContainer.appendChild(chip);
      });
    };

    // Handle tag selection
    dropdown.addEventListener("click", (e) => {
      const option = e.target.closest("[data-tag-slug]");
      if (!option) return;

      const slug = option.dataset.tagSlug;
      if (!selectedTags.includes(slug)) {
        selectedTags.push(slug);
        renderSelectedTags();
        update();
      }
      closeDropdown();
    });
  };

  const initArticleToc = () => {
    const content = document.querySelector("[data-article-content]");
    const nav = document.querySelector("[data-article-toc-nav]");
    if (!content || !nav) return;

    const emptyState = document.querySelector("[data-article-toc-empty]");

    const existingItems = nav.querySelector("ol, ul");
    if (existingItems && existingItems.children.length) {
      if (emptyState) emptyState.hidden = true;
      initTocScrollTracking(content, nav);
      return;
    }

    const headings = Array.from(content.querySelectorAll("h2, h3, h4")).filter(
      (heading) => heading.textContent.trim().length,
    );

    if (!headings.length) {
      if (emptyState) emptyState.hidden = false;
      return;
    }

    const list = document.createElement("ol");
    const slugCounts = Object.create(null);

    const ensureId = (heading) => {
      if (heading.id) return heading.id;
      const base = slugify(heading.textContent) || "section";
      const count = slugCounts[base] || 0;
      slugCounts[base] = count + 1;
      const slug = count ? `${base}-${count}` : base;
      heading.id = slug;
      return heading.id;
    };

    headings.forEach((heading) => {
      const level = parseInt(heading.tagName.slice(1), 10);
      const id = ensureId(heading);
      const item = document.createElement("li");
      item.className = `toc-item level-${level}`;

      const link = document.createElement("a");
      link.href = `#${id}`;
      link.textContent = heading.textContent.trim();
      item.appendChild(link);

      list.appendChild(item);
    });

    nav.innerHTML = "";
    nav.appendChild(list);

    if (emptyState) emptyState.hidden = true;

    initTocScrollTracking(content, nav);
  };

  const initPostViews = () => {
    const viewsContainer = document.querySelector("[data-post-views]");
    if (!viewsContainer) return;

    const postId = viewsContainer.dataset.postId;
    if (!postId) return;

    const countEl = viewsContainer.querySelector("[data-post-views-count]");
    if (!countEl) return;

    const storageKey = "axari_post_views";
    const sessionKey = "axari_viewed_posts";

    // Format the count (e.g., 1.2k for thousands)
    const formatCount = (num) => {
      if (num >= 1000000) {
        return (num / 1000000).toFixed(1).replace(/\.0$/, "") + "M";
      }
      if (num >= 1000) {
        return (num / 1000).toFixed(1).replace(/\.0$/, "") + "k";
      }
      return num.toString();
    };

    // Get all views from localStorage
    const getViews = () => {
      try {
        return JSON.parse(localStorage.getItem(storageKey) || "{}");
      } catch {
        return {};
      }
    };

    // Get viewed posts in this session
    const getViewedPosts = () => {
      try {
        return JSON.parse(sessionStorage.getItem(sessionKey) || "[]");
      } catch {
        return [];
      }
    };

    // Save views to localStorage
    const saveViews = (views) => {
      try {
        localStorage.setItem(storageKey, JSON.stringify(views));
      } catch {}
    };

    // Mark post as viewed in this session
    const markAsViewed = (id) => {
      try {
        const viewed = getViewedPosts();
        if (!viewed.includes(id)) {
          viewed.push(id);
          sessionStorage.setItem(sessionKey, JSON.stringify(viewed));
        }
      } catch {}
    };

    // Get current views
    const views = getViews();
    const currentCount = views[postId] || 0;
    const viewedPosts = getViewedPosts();

    // Increment if not already viewed in this session
    let newCount = currentCount;
    if (!viewedPosts.includes(postId)) {
      newCount = currentCount + 1;
      views[postId] = newCount;
      saveViews(views);
      markAsViewed(postId);
    }

    // Update the display
    countEl.textContent = formatCount(newCount);
  };

  const initTocScrollTracking = (content, nav) => {
    const headings = Array.from(
      content.querySelectorAll("h2, h3, h4, h5, h6"),
    ).filter((h) => h.id);
    if (!headings.length) return;

    const tocLinks = nav.querySelectorAll("a[href^='#']");
    if (!tocLinks.length) return;

    // Map heading IDs to their TOC links
    const linkMap = new Map();
    tocLinks.forEach((link) => {
      const id = link.getAttribute("href").slice(1);
      linkMap.set(id, link);
    });

    let activeId = null;

    const setActive = (id) => {
      if (activeId === id) return;

      // Remove active from previous
      if (activeId) {
        const prevLink = linkMap.get(activeId);
        if (prevLink) {
          prevLink.classList.remove("is-active");
          prevLink.closest(".toc-item")?.classList.remove("is-active");
        }
      }

      // Add active to current
      activeId = id;
      if (id) {
        const link = linkMap.get(id);
        if (link) {
          link.classList.add("is-active");
          link.closest(".toc-item")?.classList.add("is-active");
        }
      }
    };

    // Use Intersection Observer to track which heading is in view
    const observer = new IntersectionObserver(
      (entries) => {
        // Find the topmost visible heading
        const visibleEntries = entries.filter((e) => e.isIntersecting);

        if (visibleEntries.length) {
          // Sort by position in document (top to bottom)
          visibleEntries.sort(
            (a, b) => a.boundingClientRect.top - b.boundingClientRect.top,
          );
          setActive(visibleEntries[0].target.id);
        } else {
          // When scrolling up past a heading, find the last heading above viewport
          const scrollY = window.scrollY;
          let lastAbove = null;

          for (const heading of headings) {
            const rect = heading.getBoundingClientRect();
            const absoluteTop = rect.top + scrollY;
            if (absoluteTop <= scrollY + 150) {
              lastAbove = heading;
            }
          }

          if (lastAbove) {
            setActive(lastAbove.id);
          }
        }
      },
      {
        rootMargin: "-100px 0px -60% 0px",
        threshold: 0,
      },
    );

    headings.forEach((heading) => observer.observe(heading));
  };

  /* -------------------------
     Namespace & boot
     ------------------------- */
  const attachNamespace = () => {
    const ns = (window.Axari = window.Axari || {});
    ns.utils = Object.assign({ $, $$, safe, debounce }, ns.utils || {});
    ns.media = Object.assign(
      {
        requestMutedInlinePlayback,
        hydrateVideo,
        hydrateLazyVideos,
      },
      ns.media || {},
    );
    ns.handlers = Object.assign({ pauseAllOnHidden }, ns.handlers || {});
    ns.features = Object.assign(
      {
        initMobileNav,
        initNavDropdowns,
        initBlogSearch,
        initArticleToc,
        initPostViews,
        initFaqAccordion,
        initIntegrationAnimations,
        initTwinShiftAnimations,
        initTwinHeroTypewriter,
        initTwinUseCasesSlider,
        initTwinCompoundSlider,
        initTwinQuotesSlider,
        initAxRoiA,
        initTwinScrollAnimations,
        initTwinLogoMarquees,
      },
      ns.features || {},
    );
  };

  const initTwinLogoMarquees = () => {
    const tracks = $$(".twin-logo-track, .twin-integration-logos-track");
    if (!tracks.length) return;

    const reduce = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;

    if (reduce) {
      tracks.forEach((track) => track.classList.add("is-marquee-ready"));
      return;
    }

    const waitImages = (root) =>
      Promise.all(
        $$("img", root).map(
          (img) =>
            new Promise((resolve) => {
              img.loading = "eager";
              if (img.complete) {
                resolve();
                return;
              }
              img.addEventListener("load", resolve, { once: true });
              img.addEventListener("error", resolve, { once: true });
            }),
        ),
      );

    const readGroupShift = (group) => {
      const width = group.getBoundingClientRect().width;
      return width > 0 ? width : 0;
    };

    const applyShift = (track, shift) => {
      if (!(shift > 0)) return;

      const prev = parseFloat(
        track.style.getPropertyValue("--marquee-shift") || "0",
      );
      const next = `${shift}px`;
      track.style.setProperty("--marquee-shift", next);

      // Large corrections mid-animation can show a seam until the next loop; restart once.
      if (prev > 0 && Math.abs(prev - shift) > 1) {
        track.style.animation = "none";
        void track.offsetWidth;
        track.style.removeProperty("animation");
      }

      track.classList.add("is-marquee-ready");
    };

    const syncTrack = async (track) => {
      const group = track.querySelector(
        ".twin-logo-track__group, .twin-integration-logos-track__group",
      );
      if (!group) return;

      await waitImages(track);
      if (document.fonts?.ready) {
        try {
          await document.fonts.ready;
        } catch (_) {}
      }
      await new Promise((resolve) =>
        requestAnimationFrame(() => requestAnimationFrame(resolve)),
      );

      applyShift(track, readGroupShift(group));
    };

    const syncAll = () => {
      tracks.forEach((track) => {
        syncTrack(track);
      });
    };

    syncAll();

    window.addEventListener("resize", debounce(syncAll, 150));
    window.addEventListener("load", syncAll, { once: true });

    if (typeof ResizeObserver !== "undefined") {
      const ro = new ResizeObserver(
        debounce((entries) => {
          entries.forEach((entry) => {
            const group = entry.target;
            const track = group.closest(
              ".twin-logo-track, .twin-integration-logos-track",
            );
            if (!track) return;
            applyShift(track, readGroupShift(group));
          });
        }, 32),
      );

      tracks.forEach((track) => {
        const group = track.querySelector(
          ".twin-logo-track__group, .twin-integration-logos-track__group",
        );
        if (group) ro.observe(group);
      });
    }

    // Homepage ScrollTrigger runs after init; re-measure when layout settles.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        syncAll();
        scheduleScrollTriggerRefresh();
        requestAnimationFrame(syncAll);
      });
    });
  };

  const initTwinInfiniteTrackSlider = (slider, config) => {
    const {
      trackSelector,
      cardSelector,
      cloneClass,
      autoplayDelay,
      initialIndex: configInitialIndex,
      slideAriaLabel,
      align = "center",
      pauseWhenInView = false,
      loopClones = true,
      autoplayLoop = false,
      scrollIndicator = null,
      onSlideChange = null,
    } = config;

    const track = $(trackSelector, slider);
    const originals = $$(cardSelector, track);
    if (!track || !originals.length) return;

    const cardClass = cardSelector.replace(/^\./, "");
    const AUTOPLAY_DELAY = autoplayDelay;
    const count = originals.length;
    let realStart = count;

    if (loopClones) {
      for (let i = count - 1; i >= 0; i -= 1) {
        const clone = originals[i].cloneNode(true);
        clone.classList.add(cardClass, cloneClass);
        clone.setAttribute("aria-hidden", "true");
        clone.classList.remove("is-active");
        track.insertBefore(clone, track.firstChild);
      }

      originals.forEach((card) => {
        const clone = card.cloneNode(true);
        clone.classList.add(cardClass, cloneClass);
        clone.setAttribute("aria-hidden", "true");
        clone.classList.remove("is-active");
        track.appendChild(clone);
      });
    } else {
      realStart = 0;
    }

    const slides = loopClones ? $$(cardSelector, track) : originals;
    const initialIndex =
      typeof configInitialIndex === "number"
        ? Math.min(count - 1, Math.max(0, configInitialIndex))
        : loopClones
          ? Math.min(1, Math.max(0, count - 1))
          : 0;
    let logicalIndex = initialIndex;
    let scrollTimer;
    let interactionTimer;
    let autoplayTimer;
    let isProgrammaticScroll = false;
    let isPaused = false;
    let isDragging = false;
    let dragStartX = 0;
    let dragStartScroll = 0;
    let hasDragged = false;
    let isInitializing = true;
    let isInView = false;

    originals.forEach((card, i) => {
      card.setAttribute("role", "group");
      card.setAttribute("aria-roledescription", "slide");
      card.setAttribute("aria-label", slideAriaLabel(i, count));
    });

    const getLogicalForSlide = (slideIndex) => {
      if (!loopClones) return slideIndex;
      if (slideIndex < count) return slideIndex;
      if (slideIndex >= count * 2) return slideIndex - count * 2;
      return slideIndex - count;
    };

    const slideForLogical = (logical) => slides[realStart + logical];

    const getSlideContentLeft = (slide) => {
      const trackRect = track.getBoundingClientRect();
      const slideRect = slide.getBoundingClientRect();
      return slideRect.left - trackRect.left + track.scrollLeft;
    };

    const getOffsetForSlide = (slide) => {
      const padL = parseFloat(getComputedStyle(track).paddingLeft) || 0;
      const contentLeft = getSlideContentLeft(slide);
      const slideWidth = slide.getBoundingClientRect().width;

      if (align === "start") {
        return Math.max(0, contentLeft - padL);
      }

      return Math.max(0, contentLeft - (track.clientWidth - slideWidth) / 2);
    };

    const dotClass = scrollIndicator?.dotClass || "twin-use-cases__dot";

    const dotsRoot = scrollIndicator?.dotsSelector
      ? $(scrollIndicator.dotsSelector, slider)
      : null;
    const prevBtn = scrollIndicator?.prevSelector
      ? $(scrollIndicator.prevSelector, slider)
      : null;
    const nextBtn = scrollIndicator?.nextSelector
      ? $(scrollIndicator.nextSelector, slider)
      : null;

    const ensureScrollControls = () => {
      if (dotsRoot && dotsRoot.dataset.built !== "1") {
        dotsRoot.dataset.built = "1";

        for (let i = 0; i < count; i += 1) {
          const dot = document.createElement("button");
          dot.type = "button";
          dot.className = dotClass;
          dot.setAttribute("role", "tab");
          dot.setAttribute("aria-selected", "false");
          dot.setAttribute("aria-label", slideAriaLabel(i, count));
          dot.addEventListener("click", () => {
            goTo(i);
            markInteraction();
          });
          dotsRoot.appendChild(dot);
        }
      }

      if (prevBtn && prevBtn.dataset.bound !== "1") {
        prevBtn.dataset.bound = "1";
        prevBtn.addEventListener("click", () => {
          if (logicalIndex <= 0) return;
          goTo(logicalIndex - 1);
          markInteraction();
        });
      }

      if (nextBtn && nextBtn.dataset.bound !== "1") {
        nextBtn.dataset.bound = "1";
        nextBtn.addEventListener("click", () => {
          if (logicalIndex >= count - 1) return;
          goTo(logicalIndex + 1);
          markInteraction();
        });
      }
    };

    const getMaxScroll = () =>
      Math.max(0, track.scrollWidth - track.clientWidth);

    const getExpectedScrollForLogical = (idx) => {
      const slide = slideForLogical(idx);
      if (!slide) return 0;
      return Math.min(getOffsetForSlide(slide), getMaxScroll());
    };

    const isScrollAlignedWithLogical = () => {
      if (loopClones) return false;

      const maxScroll = getMaxScroll();
      if (logicalIndex >= count - 1 && track.scrollLeft >= maxScroll - 12) {
        return true;
      }
      if (logicalIndex <= 0 && track.scrollLeft <= 12) {
        return true;
      }

      const expected = getExpectedScrollForLogical(logicalIndex);
      return Math.abs(track.scrollLeft - expected) <= 28;
    };

    const resolveLogicalIndexFromScroll = () => {
      if (loopClones) return logicalIndex;

      const maxScroll = getMaxScroll();
      if (maxScroll <= 8) return logicalIndex;

      if (track.scrollLeft >= maxScroll - 8) return count - 1;
      if (track.scrollLeft <= 8) return 0;

      return Math.min(count - 1, Math.max(0, getClosestSlideIndex()));
    };

    const updateScrollIndicator = () => {
      if (!scrollIndicator) return;

      ensureScrollControls();

      const maxScroll = getMaxScroll();

      const displayedIndex =
        !loopClones && isDragging
          ? resolveLogicalIndexFromScroll()
          : logicalIndex;

      if (dotsRoot) {
        $$(`.${dotClass.replace(/^\./, "")}`, dotsRoot).forEach((dot, i) => {
          const active = i === displayedIndex;
          dot.classList.toggle("is-active", active);
          dot.setAttribute("aria-selected", active ? "true" : "false");
        });
      }

      const atFirstSlide = logicalIndex <= 0;
      const atLastSlide = logicalIndex >= count - 1;

      if (prevBtn) {
        prevBtn.disabled = atFirstSlide;
        prevBtn.classList.toggle("is-disabled", atFirstSlide);
        prevBtn.setAttribute("aria-disabled", atFirstSlide ? "true" : "false");
      }

      if (nextBtn) {
        nextBtn.disabled = atLastSlide;
        nextBtn.classList.toggle("is-disabled", atLastSlide);
        nextBtn.setAttribute("aria-disabled", atLastSlide ? "true" : "false");
      }

      const atStart = track.scrollLeft <= 4;
      const atEnd = maxScroll <= 4 || track.scrollLeft >= maxScroll - 4;
      slider.classList.toggle("is-scrollable", maxScroll > 8);
      slider.classList.toggle("is-at-scroll-start", atStart);
      slider.classList.toggle("is-at-scroll-end", atEnd);
    };

    const updateUI = () => {
      slides.forEach((slide, i) => {
        const isActive = getLogicalForSlide(i) === logicalIndex;
        slide.classList.toggle("is-active", isActive);

        if (!loopClones || !slide.classList.contains(cloneClass)) {
          slide.setAttribute("aria-hidden", isActive ? "false" : "true");
        }
      });
      updateScrollIndicator();

      if (typeof onSlideChange === "function") {
        onSlideChange(logicalIndex, slideForLogical(logicalIndex));
      }
    };

    const scrollToSlide = (slide, behavior = "smooth") => {
      isProgrammaticScroll = true;
      const prevSnap = track.style.scrollSnapType;
      track.style.scrollSnapType = "none";
      let left = Math.max(0, getOffsetForSlide(slide));
      if (!loopClones) {
        left = Math.min(left, getMaxScroll());
      }
      track.scrollTo({
        left,
        behavior,
      });
      window.setTimeout(
        () => {
          track.style.scrollSnapType = prevSnap;
          isProgrammaticScroll = false;
        },
        behavior === "auto" ? 120 : 750,
      );
    };

    const isRealSlideIndex = (index) =>
      !loopClones || (index >= count && index < count * 2);

    const getClosestSlideIndex = () => {
      let closestIdx = loopClones ? realStart : 0;
      let closestDistance = Infinity;

      if (align === "start") {
        const maxScroll = getMaxScroll();
        if (!loopClones && maxScroll > 8) {
          if (track.scrollLeft >= maxScroll - 8) return count - 1;
          if (track.scrollLeft <= 8) return 0;
        }

        const scrollTarget = track.scrollLeft;

        slides.forEach((slide, i) => {
          if (!isRealSlideIndex(i)) return;
          const distance = Math.abs(getOffsetForSlide(slide) - scrollTarget);
          if (distance < closestDistance) {
            closestDistance = distance;
            closestIdx = i;
          }
        });

        return closestIdx;
      }

      const trackCenter = track.scrollLeft + track.clientWidth / 2;

      slides.forEach((slide, i) => {
        if (!isRealSlideIndex(i)) return;
        const slideCenter =
          getSlideContentLeft(slide) + slide.getBoundingClientRect().width / 2;
        const distance = Math.abs(slideCenter - trackCenter);
        if (distance < closestDistance) {
          closestDistance = distance;
          closestIdx = i;
        }
      });

      return closestIdx;
    };

    const normalizeScroll = () => {
      if (isProgrammaticScroll || isDragging || isInitializing) return;

      if (!loopClones) {
        if (isScrollAlignedWithLogical()) {
          updateUI();
          return;
        }

        const resolved = resolveLogicalIndexFromScroll();
        if (resolved !== logicalIndex) {
          logicalIndex = resolved;
          scrollToSlide(slideForLogical(logicalIndex), "auto");
        }
        updateUI();
        return;
      }

      const closestIdx = getClosestSlideIndex();
      logicalIndex = getLogicalForSlide(closestIdx);

      const alignedSlide = slideForLogical(logicalIndex);
      if (alignedSlide) {
        const expected = getOffsetForSlide(alignedSlide);
        if (Math.abs(track.scrollLeft - expected) > 36) {
          scrollToSlide(alignedSlide, "auto");
        }
      }

      updateUI();
    };

    const goTo = (nextLogicalIndex, behavior = "smooth") => {
      const prev = logicalIndex;
      const next = loopClones
        ? ((nextLogicalIndex % count) + count) % count
        : Math.min(count - 1, Math.max(0, nextLogicalIndex));

      logicalIndex = next;

      if (!loopClones) {
        scrollToSlide(slideForLogical(next), behavior);
        updateUI();
        return;
      }

      let target = slideForLogical(next);
      let shouldRewind = false;

      if (behavior === "smooth" && prev === count - 1 && next === 0) {
        target = slides[count * 2];
        shouldRewind = true;
      } else if (behavior === "smooth" && prev === 0 && next === count - 1) {
        target = slides[count - 1];
        shouldRewind = true;
      }

      scrollToSlide(target, behavior);
      updateUI();

      if (shouldRewind) {
        window.setTimeout(() => {
          scrollToSlide(slideForLogical(logicalIndex), "auto");
          updateUI();
        }, 460);
      }
    };

    const canAutoplay = () =>
      !isPaused &&
      !document.hidden &&
      !isDragging &&
      (!pauseWhenInView || isInView);

    const stopAutoplay = () => {
      window.clearInterval(autoplayTimer);
      autoplayTimer = null;
    };

    const syncAutoplay = () => {
      if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
        stopAutoplay();
        return;
      }

      stopAutoplay();
      if (!canAutoplay()) return;

      autoplayTimer = window.setInterval(() => {
        if (!canAutoplay()) return;
        if (!loopClones && logicalIndex >= count - 1) {
          if (autoplayLoop && count > 1) {
            goTo(0);
          } else {
            stopAutoplay();
          }
          return;
        }
        goTo(logicalIndex + 1);
      }, AUTOPLAY_DELAY);
    };

    const pauseAutoplay = () => {
      isPaused = true;
      stopAutoplay();
    };

    const resumeAutoplay = () => {
      isPaused = false;
      syncAutoplay();
    };

    const markInteraction = () => {
      pauseAutoplay();
      if (pauseWhenInView) return;

      window.clearTimeout(interactionTimer);
      interactionTimer = window.setTimeout(resumeAutoplay, AUTOPLAY_DELAY);
    };

    if (pauseWhenInView) {
      const viewportRoot =
        slider.closest(".twin-quotes") ||
        slider.closest(".twin-use-cases") ||
        slider.closest(".twin-compound") ||
        slider;
      const viewportObserver = new IntersectionObserver(
        (entries) => {
          isInView = entries.some((entry) => entry.isIntersecting);
          syncAutoplay();
        },
        { threshold: 0.12 },
      );
      viewportObserver.observe(viewportRoot);
    }

    track.addEventListener(
      "scroll",
      () => {
        if (!isProgrammaticScroll && !isInitializing && isDragging) {
          markInteraction();
        } else if (
          !isProgrammaticScroll &&
          !isInitializing &&
          !pauseWhenInView
        ) {
          markInteraction();
        }

        updateScrollIndicator();

        window.clearTimeout(scrollTimer);
        scrollTimer = window.setTimeout(normalizeScroll, 80);
      },
      { passive: true },
    );

    slider.addEventListener("mouseenter", pauseAutoplay);
    slider.addEventListener("mouseleave", resumeAutoplay);
    slider.addEventListener("focusin", pauseAutoplay);
    slider.addEventListener("focusout", resumeAutoplay);
    track.addEventListener("touchstart", markInteraction, { passive: true });

    const finePointerQuery = window.matchMedia("(pointer: fine)");
    const canDragScroll = () => finePointerQuery.matches;

    const endDrag = () => {
      if (!isDragging) return;

      isDragging = false;
      track.classList.remove("is-dragging");

      if (hasDragged) {
        markInteraction();
        normalizeScroll();
      } else {
        resumeAutoplay();
      }
    };

    const onMouseDown = (event) => {
      if (!canDragScroll()) return;
      if (event.button !== 0) return;

      isDragging = true;
      hasDragged = false;
      dragStartX = event.pageX;
      dragStartScroll = track.scrollLeft;
      track.classList.add("is-dragging");
      pauseAutoplay();
    };

    const onMouseMove = (event) => {
      if (!isDragging) return;

      const deltaX = event.pageX - dragStartX;
      if (Math.abs(deltaX) > 3) {
        hasDragged = true;
      }

      track.scrollLeft = dragStartScroll - deltaX;
    };

    track.addEventListener("mousedown", onMouseDown);
    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", endDrag);
    track.addEventListener("touchcancel", endDrag, { passive: true });
    window.addEventListener("blur", endDrag);

    document.addEventListener("visibilitychange", () => {
      if (document.hidden) {
        pauseAutoplay();
      } else {
        resumeAutoplay();
      }
    });

    const scrollToStart = (behavior = "auto") => {
      logicalIndex = 0;
      isProgrammaticScroll = true;
      const prevSnap = track.style.scrollSnapType;
      track.style.scrollSnapType = "none";
      track.scrollLeft = 0;
      window.requestAnimationFrame(() => {
        track.scrollLeft = 0;
        track.style.scrollSnapType = prevSnap;
        window.setTimeout(
          () => {
            isProgrammaticScroll = false;
          },
          behavior === "auto" ? 120 : 750,
        );
      });
      updateUI();
    };

    const goToStart = (behavior = "auto") => {
      if (loopClones) {
        logicalIndex = initialIndex;
        scrollToSlide(slideForLogical(logicalIndex), behavior);
      } else if (align === "start") {
        scrollToStart(behavior);
        return;
      } else {
        logicalIndex = 0;
        const first = slideForLogical(0);
        if (first) scrollToSlide(first, behavior);
      }
      updateUI();
    };

    window.addEventListener("resize", () => {
      if (!loopClones && logicalIndex === 0) {
        goToStart("auto");
      } else {
        scrollToSlide(slideForLogical(logicalIndex), "auto");
      }
      updateUI();
    });

    if (!loopClones && align === "start") {
      logicalIndex = 0;
      track.scrollLeft = 0;
      updateUI();
    } else {
      goToStart("auto");
    }

    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        isInitializing = false;
        slider.classList.add("is-slider-ready");
        goToStart("auto");
        syncAutoplay();
      });
    });

    if (!loopClones) {
      window.addEventListener("load", () => goToStart("auto"), { once: true });
      window.addEventListener("pageshow", (event) => {
        if (event.persisted) goToStart("auto");
      });

      if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(() => goToStart("auto"));
      }

      if (typeof ResizeObserver !== "undefined") {
        let layoutAlignTimer;
        const layoutObserver = new ResizeObserver(() => {
          if (logicalIndex !== 0) return;
          window.clearTimeout(layoutAlignTimer);
          layoutAlignTimer = window.setTimeout(() => goToStart("auto"), 50);
        });
        layoutObserver.observe(track);
      }
    }
  };

  const initTwinUseCasesSlider = () => {
    const slider = $("[data-twin-use-cases-slider]");
    if (!slider) return;

    initTwinInfiniteTrackSlider(slider, {
      trackSelector: ".twin-use-cases__grid",
      cardSelector: ".twin-use-cases__card",
      cloneClass: "twin-use-cases__card--clone",
      autoplayDelay: 4000,
      initialIndex: 0,
      align: "start",
      loopClones: false,
      autoplayLoop: true,
      pauseWhenInView: true,
      slideAriaLabel: (i, count) => `Use case ${i + 1} of ${count}`,
    });
  };

  const initTwinQuotesSlider = () => {
    const slider = $("[data-twin-quotes-slider]");
    if (!slider) return;

    const section = slider.closest(".twin-quotes");
    const pull = section ? $("[data-twin-quotes-pull]", section) : null;
    let lastPullIndex = -1;

    initTwinInfiniteTrackSlider(slider, {
      trackSelector: ".twin-quotes__track",
      cardSelector: ".twin-quotes__slide",
      cloneClass: "twin-quotes__slide--clone",
      autoplayDelay: 5000,
      initialIndex: 0,
      align: "center",
      loopClones: true,
      pauseWhenInView: true,
      slideAriaLabel: (i, count) => `Customer quote ${i + 1} of ${count}`,
      onSlideChange: (index, slide) => {
        if (!pull || !slide || index === lastPullIndex) return;
        lastPullIndex = index;
        const text =
          slide.getAttribute("data-pull") ||
          $(".twin-quotes__quote", slide)?.textContent?.trim();
        if (text) pull.textContent = text;
      },
    });
  };

  const initAxRoiA = () => {
    const root = document.querySelector("[data-ax-roi-a]");
    if (!root) return;

    const reduce = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;
    const counters = $$("[data-count]", root);
    const today = $$('[data-bar="today"] .seg', root);
    const twin = $$('[data-bar="twin"] .seg', root);
    const CYCLE_MS = 10000;
    const MORPH_DELAY_MS = 1400;
    const RESET_PAUSE_MS = 1200;
    let played = false;
    let cycleTimer = null;

    const countUp = (el) => {
      const to = Number(el.dataset.count);
      const suf = el.dataset.suffix || "";
      if (reduce) {
        el.textContent = `${to}${suf}`;
        return;
      }
      const dur = 1400;
      let t0 = null;
      const step = (t) => {
        if (!t0) t0 = t;
        const p = Math.min(1, (t - t0) / dur);
        const eased = 1 - (1 - p) ** 3;
        el.textContent = `${Math.round(to * eased)}${suf}`;
        if (p < 1) window.requestAnimationFrame(step);
      };
      window.requestAnimationFrame(step);
    };

    const applySegWidths = (segs, key) => {
      segs.forEach((seg) => {
        seg.style.width = `${Number(seg.dataset[key] || 0)}%`;
      });
    };

    const stopCycle = () => {
      if (cycleTimer) {
        window.clearInterval(cycleTimer);
        cycleTimer = null;
      }
    };

    const morphTwinToTarget = () => {
      applySegWidths(twin, "w");
    };

    const replayTwinMorph = () => {
      applySegWidths(twin, "from");
      window.setTimeout(morphTwinToTarget, reduce ? 0 : RESET_PAUSE_MS);
    };

    const startCycle = () => {
      if (reduce || cycleTimer) return;
      cycleTimer = window.setInterval(replayTwinMorph, CYCLE_MS);
    };

    const play = () => {
      if (played) return;
      played = true;
      root.classList.add("is-in");
      counters.forEach(countUp);
      applySegWidths(today, "w");
      applySegWidths(twin, "from");
      window.setTimeout(
        () => {
          morphTwinToTarget();
          startCycle();
        },
        reduce ? 0 : MORPH_DELAY_MS,
      );
    };

    window.addEventListener(
      "resize",
      debounce(() => {
        if (!played) return;
        applySegWidths(today, "w");
        applySegWidths(twin, "w");
      }, 120),
    );

    document.addEventListener("visibilitychange", () => {
      if (document.hidden) {
        stopCycle();
      } else if (played && !reduce) {
        startCycle();
      }
    });

    if (reduce || !("IntersectionObserver" in window)) {
      play();
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const inView = entries.some((entry) => entry.isIntersecting);
        if (inView && !played) {
          play();
        }
        if (inView && played && !reduce) {
          startCycle();
        }
        if (!inView) {
          stopCycle();
        }
      },
      { threshold: 0.25 },
    );
    observer.observe(root);
  };

  const initTwinCompoundSlider = () => {
    const slider = $("[data-twin-compound-slider]");
    if (!slider) return;

    initTwinInfiniteTrackSlider(slider, {
      trackSelector: ".twin-compound__track",
      cardSelector: ".twin-compound__step",
      cloneClass: "twin-compound__step--clone",
      autoplayDelay: 5000,
      initialIndex: 0,
      align: "start",
      loopClones: false,
      pauseWhenInView: true,
      slideAriaLabel: (i, count) => `How it works step ${i + 1} of ${count}`,
    });
  };

  const initTwinHeroTypewriter = () => {
    const root = document.querySelector("[data-twin-hero-typewriter]");
    if (!root) return;

    const textEl = $(".twin-hero__typewriter-text", root);
    if (!textEl) return;

    const cursorEl = $(".twin-hero__typewriter-cursor", root);
    let phrases = ["Indefatigable.", "24/7 coverage."];

    if (root.dataset.phrases) {
      try {
        const parsed = JSON.parse(root.dataset.phrases);
        if (Array.isArray(parsed) && parsed.length) {
          phrases = parsed.map(String);
        }
      } catch (_) {
        /* keep defaults */
      }
    }

    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      textEl.textContent = phrases[0];
      if (cursorEl) cursorEl.hidden = true;
      return;
    }

    const typeMs = 42;
    const deleteMs = 26;
    const holdMs = 2400;
    const betweenMs = 360;
    let phraseIndex = 0;
    let charIndex = 0;
    let deleting = false;
    let timer;
    let started = false;

    const clearTimer = () => {
      window.clearTimeout(timer);
      timer = null;
    };

    const tick = () => {
      if (document.hidden) {
        clearTimer();
        return;
      }

      const phrase = phrases[phraseIndex];

      if (!deleting) {
        charIndex += 1;
        textEl.textContent = phrase.slice(0, charIndex);

        if (charIndex >= phrase.length) {
          deleting = true;
          timer = window.setTimeout(tick, holdMs);
          return;
        }

        timer = window.setTimeout(tick, typeMs);
        return;
      }

      charIndex -= 1;
      textEl.textContent = phrase.slice(0, charIndex);

      if (charIndex <= 0) {
        deleting = false;
        phraseIndex = (phraseIndex + 1) % phrases.length;
        timer = window.setTimeout(tick, betweenMs);
        return;
      }

      timer = window.setTimeout(tick, deleteMs);
    };

    const resume = () => {
      if (!started || document.hidden || timer) return;
      tick();
    };

    const start = () => {
      started = true;
      clearTimer();
      phraseIndex = 0;
      charIndex = 0;
      deleting = false;
      textEl.textContent = "";
      if (document.hidden) return;
      tick();
    };

    window.setTimeout(start, 720);

    document.addEventListener("visibilitychange", () => {
      if (document.hidden) clearTimer();
      else resume();
    });

    window.addEventListener("pageshow", (event) => {
      if (event.persisted) resume();
    });
  };

  const initTwinShiftAnimations = () => {
    const home = $(".twin-home");
    if (!home) return;

    const heroCopy = $(".twin-hero__copy", home);
    const heroVisuals = $(".twin-hero__visuals", home);
    const portraits = heroVisuals
      ? $$(".twin-hero__venn", heroVisuals)
      : [];
    const dayCard = $(".twin-shift--day", home);
    const nightCard = $(".twin-shift--night", home);
    const finish = () => home.classList.add("twin-home--shift-ready");

    if (
      !window.gsap ||
      window.matchMedia("(prefers-reduced-motion: reduce)").matches
    ) {
      finish();
      return;
    }

    if (!heroCopy && !portraits.length && !dayCard && !nightCard) {
      finish();
      return;
    }

    const buildCardTimeline = (card) => {
      const head = $(".twin-shift__head", card);
      const items = $$(".twin-shift__list li", card);

      gsap.set(card, { opacity: 0, y: 12 });
      gsap.set([head, ...items].filter(Boolean), {
        opacity: 0,
        y: 8,
      });

      const tl = gsap.timeline({ defaults: { ease: "power2.out" } });

      tl.to(card, { opacity: 1, y: 0, duration: 0.35 }, 0);

      if (head) {
        tl.to(head, { opacity: 1, y: 0, duration: 0.22 }, 0.1);
      }

      if (items.length) {
        tl.to(
          items,
          { opacity: 1, y: 0, stagger: 0.05, duration: 0.22 },
          "-=0.04",
        );
      }

      return tl;
    };

    const master = gsap.timeline({
      delay: 0.08,
      onComplete: finish,
    });

    const addShiftCards = (position) => {
      if (!dayCard && !nightCard) return;
      if (dayCard) master.add(buildCardTimeline(dayCard), position);
      if (nightCard) {
        master.add(buildCardTimeline(nightCard), dayCard ? "<" : position);
      }
    };

    if (heroCopy) {
      const heroParts = [
        $(".twin-hero__eyebrow", heroCopy),
        $("h1", heroCopy),
        $(".twin-hero__actions", heroCopy),
      ].filter(Boolean);

      gsap.set(heroParts, { opacity: 0, y: 24 });

      master.to(
        heroParts,
        {
          opacity: 1,
          y: 0,
          stagger: 0.1,
          duration: 0.55,
          ease: "power2.out",
        },
        0,
      );
    }

    if (portraits.length) {
      portraits.forEach((portrait, index) => {
        gsap.set(portrait, {
          opacity: 0,
          y: 28,
          x: index === 0 ? -14 : 14,
        });
      });

      master.to(
        portraits,
        {
          opacity: 1,
          y: 0,
          x: 0,
          stagger: 0.08,
          duration: 0.4,
          ease: "power2.out",
        },
        heroCopy ? 0.1 : 0,
      );

      addShiftCards(">");
    } else {
      addShiftCards(heroCopy ? ">" : 0);
    }
  };

  const initTwinLooksPersonaDemo = () => {
    const root = $("[data-twin-looks-demo]");
    if (!root) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    const { AxariPersonaDemo } = window;
    if (!AxariPersonaDemo?.create || !AxariPersonaDemo?.extractConfigFromHtml) {
      return;
    }

    const host = $("[data-persona-demo-host]", root);
    const tabs = $$(".hero-tab", root);
    if (!host || !tabs.length) return;

    const initialSrc =
      tabs.find((tab) => tab.classList.contains("active"))?.dataset.src || null;
    if (!initialSrc) return;

    const configCache = new Map();
    let activeDemo = null;
    let loadingSrc = null;
    let personaSwitching = false;

    const pausePersonaDemo = () => {
      activeDemo?.pause?.();
    };

    const fetchPersonaConfig = async (src) => {
      if (configCache.has(src)) return configCache.get(src);
      const res = await fetch(src, { credentials: "same-origin" });
      if (!res.ok) throw new Error(`Persona load failed: ${res.status}`);
      const html = await res.text();
      const config = AxariPersonaDemo.extractConfigFromHtml(html);
      if (!config) throw new Error("Persona config parse failed");
      configCache.set(src, config);
      return config;
    };

    const mountPersona = async (src) => {
      if (!src || loadingSrc === src) return;
      const currentSrc = host.dataset.personaSrc;
      if (currentSrc === src && activeDemo) return;
      loadingSrc = src;
      personaSwitching = true;
      pausePersonaDemo();
      try {
        const config = await fetchPersonaConfig(src);
        if (activeDemo?.fadeOut) {
          await new Promise((resolve) => activeDemo.fadeOut(resolve));
        }
        activeDemo?.destroy?.();
        activeDemo = AxariPersonaDemo.create(host, config);
        host.dataset.personaSrc = src;
        if (document.hidden) pausePersonaDemo();
        scheduleScrollTriggerRefresh();
      } catch (err) {
        console.warn("[twin-looks] persona demo", err);
      } finally {
        loadingSrc = null;
        personaSwitching = false;
      }
    };

    const startDemo = () => {
      if (host.dataset.loaded) return;
      host.dataset.loaded = "1";
      mountPersona(initialSrc);
    };

    if ("IntersectionObserver" in window) {
      const observer = new IntersectionObserver(
        (entries) => {
          if (entries.some((entry) => entry.isIntersecting)) {
            startDemo();
            observer.disconnect();
          }
        },
        { rootMargin: "320px 0px" },
      );
      observer.observe(root);
    } else {
      startDemo();
    }

    const activateHeroTab = (tab) => {
      if (!tab) return;
      tabs.forEach((t) => {
        const active = t === tab;
        t.classList.toggle("active", active);
        t.setAttribute("aria-selected", active ? "true" : "false");
      });
      if (tab.dataset.src && host.dataset.loaded) {
        mountPersona(tab.dataset.src);
      }
    };

    tabs.forEach((tab) => {
      tab.addEventListener("click", () => activateHeroTab(tab));
    });

    host.addEventListener("selectstart", (e) => e.preventDefault());
    host.addEventListener("dragstart", (e) => e.preventDefault());
  };

  const initTwinFinalCtaReveal = () => {
    const finalCta = $(".twin-final-cta");
    if (!finalCta) return;

    const inner = $(".twin-final-cta__inner", finalCta);
    if (!inner) return;

    const markRevealed = () => {
      finalCta.classList.add("twin-final-cta--revealed");
    };

    const revealInstant = () => {
      markRevealed();
      gsap?.killTweensOf(inner);
      if (window.gsap) {
        gsap.set(inner, { opacity: 1, y: 0, clearProps: "transform" });
      }
    };

    if (
      !window.gsap ||
      !window.ScrollTrigger ||
      window.matchMedia("(prefers-reduced-motion: reduce)").matches
    ) {
      markRevealed();
      return;
    }

    gsap.registerPlugin(ScrollTrigger);

    let revealed = false;
    let scrollTrigger = null;

    const runRevealAnimation = () => {
      if (revealed) return;
      revealed = true;
      scrollTrigger?.kill();
      gsap.to(inner, {
        opacity: 1,
        y: 0,
        duration: 0.65,
        ease: "power2.out",
        overwrite: true,
        onComplete: markRevealed,
      });
    };

    gsap.set(inner, { opacity: 1, y: 28 });

    const start = "top 88%";
    scrollTrigger = ScrollTrigger.create({
      trigger: finalCta,
      start,
      once: true,
      onEnter: runRevealAnimation,
    });

    const shouldRevealNow = () => {
      if (revealed) return;
      if (scrollTrigger && (scrollTrigger.progress > 0 || scrollTrigger.isActive)) {
        runRevealAnimation();
        return;
      }
      const rect = finalCta.getBoundingClientRect();
      const vh = window.innerHeight || document.documentElement.clientHeight;
      if (rect.top <= vh * 0.88 && rect.bottom > 0) {
        runRevealAnimation();
      }
    };

    shouldRevealNow();
    scrollRevealChecks.push(shouldRevealNow);
    bindScrollRevealRefresh();

    window.addEventListener(
      "load",
      () => {
        scheduleScrollTriggerRefresh();
        shouldRevealNow();
        window.setTimeout(shouldRevealNow, 400);
      },
      { once: true },
    );

    window.setTimeout(() => {
      if (revealed) return;
      const opacity = gsap.getProperty(inner, "opacity");
      if (typeof opacity === "number" && opacity < 0.05) {
        const rect = finalCta.getBoundingClientRect();
        const vh = window.innerHeight || document.documentElement.clientHeight;
        if (rect.top < vh && rect.bottom > 0) {
          revealInstant();
        }
      }
    }, 3500);
  };

  const initTwinScrollAnimations = () => {
    const home = $(".twin-home");
    if (!home) return;

    const finish = () => home.classList.add("twin-home--scroll-ready");

    if (
      !window.gsap ||
      !window.ScrollTrigger ||
      window.matchMedia("(prefers-reduced-motion: reduce)").matches
    ) {
      finish();
      return;
    }

    gsap.registerPlugin(ScrollTrigger);

    const fromBelow = { opacity: 0, y: 32 };
    const ease = "power2.out";
    const start = "top 88%";

    const revealOnScroll = (trigger, targets, options = {}) => {
      if (!trigger) return;
      const els = (Array.isArray(targets) ? targets : [targets])
        .flat()
        .filter(Boolean);
      if (!els.length) return;

      const hideOpacity = options.hideOpacity !== false;
      const fromY = options.y ?? 32;
      const revealStart = options.start ?? start;

      gsap.set(
        els,
        hideOpacity ? { opacity: 0, y: fromY } : { opacity: 1, y: fromY },
      );

      let revealed = false;
      let scrollTrigger = null;

      const runReveal = () => {
        if (revealed) return;
        revealed = true;
        scrollTrigger?.kill();
        gsap.to(els, {
          opacity: 1,
          y: 0,
          duration: options.duration ?? 0.55,
          ease,
          stagger: options.stagger ?? 0.08,
          delay: options.delay ?? 0,
          overwrite: true,
        });
      };

      scrollTrigger = ScrollTrigger.create({
        trigger,
        start: revealStart,
        once: true,
        onEnter: runReveal,
      });

      const shouldRevealNow = () => {
        if (revealed) return;
        if (
          scrollTrigger &&
          (scrollTrigger.progress > 0 || scrollTrigger.isActive)
        ) {
          runReveal();
          return;
        }
        const rect = trigger.getBoundingClientRect();
        const vh = window.innerHeight || document.documentElement.clientHeight;
        if (rect.top <= vh * 0.88 && rect.bottom > 0) {
          runReveal();
        }
      };

      shouldRevealNow();
      scrollRevealChecks.push(shouldRevealNow);
    };

    const revealTimeline = (trigger, buildTimeline, options = {}) => {
      if (!trigger) return;

      ScrollTrigger.create({
        trigger,
        start: options.start ?? start,
        once: true,
        onEnter: () => {
          const tl = gsap.timeline({ defaults: { ease, duration: 0.5 } });
          buildTimeline(tl);
        },
      });
    };

    const headingChildren = (section) => {
      const heading = $(".twin-heading", section);
      return heading ? Array.from(heading.children) : [];
    };

    const looks = $(".twin-looks", home);
    if (looks) {
      revealOnScroll(looks, $(".twin-looks__kicker", looks), { y: 24 });
      revealOnScroll(looks, $(".twin-looks__mock", looks), {
        y: 28,
        delay: 0.04,
        hideOpacity: false,
      });
      revealTimeline(looks, (tl) => {
        const quotes = $(".twin-looks__quotes", looks);
        const logos = $(".twin-looks__logos", looks);
        if (quotes) {
          gsap.set(quotes, fromBelow);
          tl.to(quotes, { opacity: 1, y: 0, duration: 0.5 }, 0);
        }
        if (logos) {
          gsap.set(logos, { ...fromBelow, y: 28 });
          tl.to(logos, { opacity: 1, y: 0, duration: 0.5 }, "-=0.28");
        }
      });
    }

    const useCases = $(".twin-use-cases", home);
    if (useCases) {
      revealOnScroll(useCases, $(".twin-use-cases__slider", useCases), {
        y: 36,
      });
    }

    const learning = $(".twin-learning", home);
    if (learning) {
      revealTimeline(learning, (tl) => {
        const band = $(".twin-logo-band", learning);
        if (band) {
          gsap.set(band, fromBelow);
          tl.to(band, { opacity: 1, y: 0, duration: 0.55 }, 0);
        }
      });
    }

    const compound = $(".twin-compound", home);
    if (compound) {
      revealTimeline(compound, (tl) => {
        const heading = headingChildren(compound);
        const steps = $$(".twin-compound__step", compound);
        if (heading.length) {
          gsap.set(heading, { ...fromBelow, y: 24 });
          tl.to(heading, { opacity: 1, y: 0, stagger: 0.08, duration: 0.5 }, 0);
        }
        if (steps.length) {
          gsap.set(steps, { ...fromBelow, y: 40 });
          tl.to(
            steps,
            { opacity: 1, y: 0, stagger: 0.12, duration: 0.5 },
            "-=0.2",
          );
        }
      });
    }

    const trusted = $(".twin-trusted", home);
    if (trusted) {
      revealTimeline(trusted, (tl) => {
        const heading = headingChildren(trusted);
        const cards = $$(".twin-trusted__card", trusted);
        if (heading.length) {
          gsap.set(heading, { ...fromBelow, y: 24 });
          tl.to(heading, { opacity: 1, y: 0, stagger: 0.08, duration: 0.5 }, 0);
        }
        if (cards.length) {
          gsap.set(cards, { ...fromBelow, y: 36 });
          tl.to(
            cards,
            { opacity: 1, y: 0, stagger: 0.1, duration: 0.48 },
            "-=0.15",
          );
        }
      });
    }

    const runScrollRevealChecks = () => {
      scrollRevealChecks.forEach((check) => check());
    };

    window.addEventListener(
      "load",
      () => {
        scheduleScrollTriggerRefresh();
        runScrollRevealChecks();
        window.setTimeout(runScrollRevealChecks, 400);
      },
      { once: true },
    );

    bindScrollRevealRefresh();

    finish();
    ScrollTrigger.refresh();
    runScrollRevealChecks();
  };

  const initIntegrationAnimations = () => {
    if (!window.gsap || !window.ScrollTrigger) return;

    gsap.registerPlugin(ScrollTrigger);

    // Landing page hero — text and image animate together, categories wait
    const landingHero = $(".integrations-hero");
    if (landingHero) {
      const heroText = $(".integrations-hero-text");
      const heroImage = $(".integrations-hero-image");
      const heroTl = gsap.timeline({
        delay: 0.1,
        onComplete: initLandingCategories,
      });

      if (heroText) {
        gsap.set(heroText.children, { y: 40 });
        heroTl.to(
          heroText.children,
          {
            opacity: 1,
            y: 0,
            stagger: 0.1,
            duration: 0.6,
            ease: "power2.out",
          },
          0,
        );
      }
      if (heroImage) {
        gsap.set(heroImage, { x: 60 });
        heroTl.to(
          heroImage,
          {
            opacity: 1,
            x: 0,
            duration: 0.7,
            ease: "power2.out",
          },
          0,
        );
      }
    } else {
      initLandingCategories();
    }

    function initLandingCategories() {
      const categories = $$(".integrations-category");
      if (!categories.length) return;

      categories.forEach((category) => {
        const title = $(".integrations-category-title", category);
        const cards = $$(".integration-card", category);

        if (title) gsap.set(title, { y: 30 });
        cards.forEach((c) => gsap.set(c, { y: 40 }));

        ScrollTrigger.create({
          trigger: category,
          start: "top bottom-=15%",
          once: true,
          onEnter: () => {
            const tl = gsap.timeline({ defaults: { ease: "power2.out" } });
            if (title) tl.to(title, { opacity: 1, y: 0, duration: 0.35 });
            if (cards.length)
              tl.to(
                cards,
                { opacity: 1, y: 0, stagger: 0.06, duration: 0.3 },
                "-=0.15",
              );
          },
        });
      });

      ScrollTrigger.refresh();
    }

    // Detail page hero
    const detailHero = $(".integration-detail-hero");
    if (detailHero) {
      const heroText = $(".integration-detail-hero-text");
      const heroImage = $(".integration-detail-hero-image");
      if (heroText) {
        gsap.set(heroText.children, { y: 40 });
        gsap.to(heroText.children, {
          opacity: 1,
          y: 0,
          stagger: 0.15,
          duration: 0.8,
          ease: "power2.out",
          delay: 0.2,
        });
      }
      if (heroImage) {
        gsap.set(heroImage, { scale: 0.9 });
        gsap.to(heroImage, {
          opacity: 1,
          scale: 1,
          duration: 1,
          ease: "power2.out",
          delay: 0.5,
        });
      }
    }

    // Detail page sections
    $$(".integration-section").forEach((section) => {
      const header = $(".integration-section-header", section);
      const features = $$(".integration-feature", section);
      const note = $(".integration-section-note", section);

      if (header) {
        gsap.set(header.children, { opacity: 0, y: 30 });
      }
      features.forEach((f) => gsap.set(f, { opacity: 0, y: 40 }));
      if (note) gsap.set(note, { opacity: 0, y: 20 });

      ScrollTrigger.create({
        trigger: section,
        start: "top bottom-=15%",
        once: true,
        onEnter: () => {
          const tl = gsap.timeline({ defaults: { ease: "power2.out" } });
          if (header)
            tl.to(header.children, {
              opacity: 1,
              y: 0,
              stagger: 0.1,
              duration: 0.6,
            });
          if (features.length)
            tl.to(
              features,
              { opacity: 1, y: 0, stagger: 0.12, duration: 0.5 },
              "-=0.3",
            );
          if (note) tl.to(note, { opacity: 1, y: 0, duration: 0.4 }, "-=0.2");
        },
      });
    });

    // Detail page FAQ
    const faqSection = $(".integration-faq");
    if (faqSection) {
      const faqLeft = $(".integration-faq-left", faqSection);
      const faqItems = $$(".integration-faq-item", faqSection);

      if (faqLeft) gsap.set(faqLeft.children, { opacity: 0, y: 30 });
      faqItems.forEach((f) => gsap.set(f, { opacity: 0, y: 30 }));

      ScrollTrigger.create({
        trigger: faqSection,
        start: "top bottom-=15%",
        once: true,
        onEnter: () => {
          const tl = gsap.timeline({ defaults: { ease: "power2.out" } });
          if (faqLeft)
            tl.to(faqLeft.children, {
              opacity: 1,
              y: 0,
              stagger: 0.15,
              duration: 0.6,
            });
          if (faqItems.length)
            tl.to(
              faqItems,
              { opacity: 1, y: 0, stagger: 0.12, duration: 0.5 },
              "-=0.3",
            );
        },
      });
    }
  };

  const initFaqAccordion = () => {
    document.querySelectorAll(".integration-faq-item h3").forEach((trigger) => {
      trigger.addEventListener("click", () => {
        const item = trigger.closest(".integration-faq-item");
        item.classList.toggle("is-open");
      });
    });
  };

  const boot = () => {
    hydrateLazyVideos();
    initProgressiveImages();
    initNavCurrentLink();
    initMobileNav();
    initNavDropdowns();
    initScrollNav();
    initBlogSearch();
    initArticleToc();
    initPostViews();
    initFaqAccordion();
    initIntegrationAnimations();
    initTwinShiftAnimations();
    initTwinHeroTypewriter();
    initTwinLooksPersonaDemo();
    initTwinUseCasesSlider();
    initTwinCompoundSlider();
    initTwinQuotesSlider();
    initAxRoiA();
    initTwinScrollAnimations();
    initTwinLogoMarquees();
    initTwinFinalCtaReveal();
    if (typeof window.initAxFaq === "function") {
      window.initAxFaq();
    }
    pauseAllOnHidden();

    window.requestAnimationFrame(() => {
      scheduleScrollTriggerRefresh();
    });
  };

  attachNamespace();

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }
})();
